import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import api from '../../services/api';

const AdminActivityLog = () => {
  const { isDark } = useTheme();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  
  useEffect(() => {
    fetchActivities();
  }, [page]);
  
  const fetchActivities = async () => {
    try {
      const response = await api.get('/api/admin/activity-log', {
        params: { limit: 50, offset: page * 50 }
      });
      
      if (page === 0) {
        setActivities(response.data.activities);
      } else {
        setActivities(prev => [...prev, ...response.data.activities]);
      }
      
      setHasMore(response.data.activities.length === 50);
    } catch (error) {
      console.error('Failed to fetch activity log:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const formatAction = (action) => {
    const actionMap = {
      'view_users': 'Viewed users list',
      'impersonate': 'Impersonated user',
      'generate_invite_code': 'Generated invite code',
      'view_invite_codes': 'Viewed invite codes',
      'view_stats': 'Viewed platform stats',
      'change_user_role': 'Changed user role'
    };
    return actionMap[action] || action;
  };
  
  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };
  
  const getActionIcon = (action) => {
    switch (action) {
      case 'impersonate':
        return (
          <svg className="w-4 h-4 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        );
      case 'generate_invite_code':
        return (
          <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        );
      case 'change_user_role':
        return (
          <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };
  
  if (loading && activities.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-400"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="glass-card p-6 rounded-xl">
        <h2 className={`heading-mono text-xl mb-6 ${isDark ? 'text-white' : 'text-gray-800'}`}>Admin Activity Log</h2>
        
        <div className="space-y-3">
          {activities.map((activity) => (
            <div key={activity.id} className="glass-dark p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="mt-1">
                  {getActionIcon(activity.action)}
                </div>
                
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-mono font-medium">
                        {formatAction(activity.action)}
                      </p>
                      <p className={`text-mono text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        by {activity.admin_email || 'Unknown'}
                        {activity.target_email && (
                          <span> • Target: {activity.target_email}</span>
                        )}
                      </p>
                      {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                        <div className={`text-mono text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                          {activity.metadata.code && <div>Code: {activity.metadata.code}</div>}
                          {activity.metadata.newRole && <div>New Role: {activity.metadata.newRole}</div>}
                          {activity.metadata.page && <div>Page: {activity.metadata.page}</div>}
                          {activity.metadata.search && <div>Search: {activity.metadata.search}</div>}
                        </div>
                      )}
                    </div>
                    
                    <span className={`text-mono text-xs ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                      {formatDate(activity.created_at)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {activities.length === 0 && (
          <p className={`text-mono text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
            No admin activities recorded yet.
          </p>
        )}
        
        {hasMore && activities.length > 0 && (
          <div className="mt-6 text-center">
            <button
              onClick={() => setPage(page + 1)}
              className="btn-glass px-6 py-2 text-mono"
            >
              Load More
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminActivityLog;